<template>
  <div>
    <div class="container">
      <h3 class="title">CHL Analytics</h3>

      <b-card style="text-align: left" bg-variant="light">
        <b-form @submit.prevent="searchRecords">
          <b-form-group
            label="Search Records:"
            description="You can search by business name, location name, plot name or barcode.">
            <b-form-input :required="!advancedSearch" v-model="string" placeholder="search records"></b-form-input>
          </b-form-group>
          <div style="width: 100%; padding-bottom: 2em; margin-top: -2em">
            <b-link
              class="advanced"
              @click="
                advancedSearch = !advancedSearch;
                showDataSets = false;
              "
              >Advanced Search</b-link
            >
            <b-link
              class="advanced mr-3"
              @click="
                showDataSets = !showDataSets;
                advancedSearch = false;
              "
              >Show Data Sets</b-link
            >
          </div>

          <b-collapse v-model="advancedSearch" class="mb-1">
            <b-card>
              <b-row>
                <b-col class="mb-2">
                  <b-input-group>
                    <template v-slot:prepend>
                      <b-form-select v-model="dateSearchType" variant="outline-secondary">
                        <option>Date Within</option>
                        <option>Date Between</option>
                      </b-form-select>
                    </template>

                    <b-form-select
                      v-show="dateSearchType === 'Date Within'"
                      v-model="dateWithin"
                      variant="outline-secondary">
                      <option>1 Day</option>
                      <option>3 Days</option>
                      <option>1 Week</option>
                      <option>2 Weeks</option>
                      <option>1 Month</option>
                      <option>2 Months</option>
                      <option>3 Months</option>
                      <option>4 Months</option>
                      <option>5 Months</option>
                      <option>6 Months</option>
                      <option>8 Months</option>
                      <option>10 Months</option>
                      <option>12 Months</option>
                    </b-form-select>

                    <b-form-input
                      v-show="dateSearchType !== 'Date Within'"
                      v-model="dateFrom"
                      type="date"></b-form-input>
                    <b-form-input v-model="dateTo" type="date"></b-form-input>
                  </b-input-group>
                </b-col>
                <b-col class="mb-2">
                  <b-input-group prepend="Business Name">
                    <b-form-input v-model="businessName"></b-form-input>
                  </b-input-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col class="mb-2">
                  <b-input-group prepend="Location Name">
                    <b-form-input v-model="locationName"></b-form-input>
                  </b-input-group>
                </b-col>
                <b-col class="mb-2">
                  <b-input-group prepend="Plot Name">
                    <b-form-input v-model="plotName"></b-form-input>
                  </b-input-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col class="mb-2">
                  <b-input-group prepend="Crop Name">
                    <b-form-input v-model="cropName"></b-form-input>
                  </b-input-group>
                </b-col>
                <b-col class="mb-2">
                  <b-input-group prepend="Variety Name">
                    <b-form-input v-model="varietyName"></b-form-input>
                  </b-input-group>
                </b-col>
              </b-row>
              <hr />
              <p class="instructions" style="margin-bottom: 0.1em">
                Advanced search now supports OR statements in all availble fields, i.e. searching 'back 40 OR front 40'
                in the location name field will return all results with 'back 40' and 'front 40' in the location name.
              </p>
            </b-card>
          </b-collapse>

          <b-collapse v-model="showDataSets" class="mb-1">
            <b-card>
              <div class="data-set-row" v-for="row of displayedDataSets" :key="row.id">
                <div class="row">
                  <div class="col-sm" style="font-size: 0.85em">{{ row.title }}</div>
                  <div class="col-sm">
                    <b-button @click="multiPdf(row)" size="sm" variant="success">Multi PDF</b-button>
                    <b-button @click="linePdf(row)" size="sm ml-1" variant="success">Line Graph</b-button>
                    <b-button @click="fetchDataSet(row, true)" size="sm ml-1" variant="success">Fetch Data</b-button>
                    <b-button
                      @click="
                        currentDataSet = row;
                        $bvModal.show('edit-data-set');
                      "
                      size="sm ml-1"
                      variant="success"
                      v-b-popover.hover.top="'save'">
                      <b-icon-cloud-arrow-up-fill></b-icon-cloud-arrow-up-fill>
                    </b-button>
                    <b-button
                      size="sm ml-1"
                      variant="danger"
                      v-b-popover.hover.top="'delete'"
                      @click="deleteDataSet(row.id)">
                      <b-icon-trash-fill></b-icon-trash-fill>
                    </b-button>
                  </div>
                </div>
              </div>
              <b-form-checkbox switch size="sm" v-model="instructions">instructions</b-form-checkbox>
              <div v-show="instructions">
                <p class="instructions">
                  A dataset allows you to group results from a given plot. Create datasets by selecting tests from one
                  or more plots, you only need to select one set per plot. Click the below button and follow the prompts
                  to create temporary datasets.
                </p>
                <p class="instructions">
                  A temporary dataset can be used in the advanced graphing page but will not persist over page reload.
                  Hit the above cloud icon to save a dataset. Saved datasets will automatically pull in newer results as
                  they arrive.
                </p>
              </div>
            </b-card>
          </b-collapse>

          <b-button size="sm" variant="purple" type="submit">Search</b-button>
          <b-button size="sm" variant="purple" @click="searchDataSets">Search Datasets Only</b-button>
        </b-form>
        <p></p>

        <b-alert :show="notify.show" variant="warning" dismissible @dismissed="notify.show = false">{{
          notify.message
        }}</b-alert>

        <b-pagination
          v-show="rows > 0"
          align="fill"
          :per-page="perPage"
          v-model="currentPage"
          :total-rows="rows"></b-pagination>
        <b-select
          class="m-1"
          style="width: 10em"
          v-model="perPage"
          :options="['15', '25', '50', '75', '100', '150']"></b-select>
        <b-button
          class="m-1"
          size="sm"
          @click="
            currentSelect = true;
            $refs.searchRes.selectAllRows();
          "
          >Select All</b-button
        >
        <b-button
          class="m-1"
          size="sm"
          @click="
            currentSelect = false;
            $refs.searchRes.selectAllRows();
          "
          >Deselect All</b-button
        >
        <b-button class="m-1" size="sm" @click="massPdfs">Mass Pdf</b-button>
        <b-button class="m-1" size="sm" @click="$bvModal.show('create-data-sets')">Create Data Sets</b-button>

        <b-table
          :sort-compare="sortCompare"
          v-if="concatOrders.length > 0"
          ref="searchRes"
          :fields="fields"
          :items="concatOrders"
          :per-page="perPage"
          :current-page="currentPage"
          @row-selected="tableSelect"
          selectable
          striped
          hover
          small
          responsive="sm">
          <template v-slot:cell(show_details)="row">
            <div style="display: inline-flex">
              <b-button size="sm" @click="row.toggleDetails" class="mr-1">{{
                row.detailsShowing ? "less" : "more"
              }}</b-button>
              <b-button
                :disabled="!row.item.values || !row.item.values[0]"
                size="sm"
                class="mr-1"
                @click="openPDFModal(row.item.externalId)"
                >pdf</b-button
              >
              <b-button size="sm" class="mr-1" @click="editRecord(row.item.externalId)">
                <b-icon-pen-fill></b-icon-pen-fill>
              </b-button>

              <div class="m-1">
                <b-icon-check-lg variant="success" v-show="row.item.selected"></b-icon-check-lg>
                <b-icon-plus-lg v-show="!row.item.selected"></b-icon-plus-lg>
              </div>
            </div>
          </template>

          <template v-slot:row-details="row">
            <b-card>
              <b-row class="mb-2">
                <b-col sm="3" class="text-sm-right">
                  <b>Remarks:</b>
                </b-col>
                <b-col>{{ row.item.comments }}</b-col>
                <b-col sm="3" class="text-sm-right">
                  <b-button variant="danger" size="sm" v-if="isAdmin" @click="deleteRow(row.item.id)" class="mr-2"
                    >delete set</b-button
                  >
                  <b-button size="sm" class="mr-2" @click="editInfo(row.item.setId)">edit info</b-button>
                </b-col>
              </b-row>

              <b-row class="mb-2">
                <b-col sm="3" class="text-sm-right">
                  <b>Plant Part:</b>
                </b-col>
                <b-col
                  >{{ row.item.plantPart }}
                  <b-icon-pen-fill v-if="isAdmin" @click="editPlantPartModal(row.item)"></b-icon-pen-fill>
                </b-col>

                <b-col sm="3" class="text-sm-right">
                  <b>Crop State:</b>
                </b-col>
                <b-col>{{ row.item.disease }}</b-col>
              </b-row>

              <b-row class="mb-2">
                <b-col sm="3" class="text-sm-right">
                  <b>Health Score:</b>
                </b-col>
                <b-col>{{ row.item.healthScore }}</b-col>

                <b-col sm="3" class="text-sm-right">
                  <b>Treatment:</b>
                </b-col>
                <b-col>{{ row.item.treatment }}</b-col>
              </b-row>

              <b-row class="mb-2">
                <b-col sm="3" class="text-sm-right">
                  <b>Phenological Phase:</b>
                </b-col>
                <b-col>{{ row.item.phenologicalPhase }}</b-col>

                <b-col sm="3" class="text-sm-right">
                  <b>Lab Analysis Date:</b>
                </b-col>
                <b-col>{{ formatDate(row.item.sampleDate) }}</b-col>
              </b-row>

              <b-row class="mb-2">
                <b-col sm="3" class="text-sm-right">
                  <b>Sample Number:</b>
                </b-col>
                <b-col>{{ row.item.sampleNumber }}</b-col>

                <b-col sm="3" class="text-sm-right">
                  <b>Leaf Collection Date:</b>
                </b-col>
                <b-col>{{ formatDate(row.item.samplingDate) }}</b-col>
              </b-row>

              <b-row class="mb-2">
                <b-col sm="3" class="text-sm-right">
                  <b>Barcode:</b>
                </b-col>
                <b-col>{{ row.item.barcode }}</b-col>
                <b-col sm="3" class="text-sm-right">
                  <b>Lab:</b>
                </b-col>
                <b-col>{{ row.item.labName ? row.item.labName : "chl" }}</b-col>
              </b-row>

              <b-row class="mb-2" v-if="isAdmin">
                <b-col sm="3" class="text-sm-right">
                  <b>Id:</b>
                </b-col>
                <b-col>{{ row.item.id }}</b-col>
                <!-- <b-col>{{ row.item.setId }}</b-col> -->
              </b-row>
            </b-card>
          </template>

          <template v-slot:cell(sampleDate)="row">{{ new Date(row.item.sampleDate).toLocaleDateString() }}</template>
        </b-table>
        <b-pagination
          v-show="rows > 0"
          align="fill"
          :per-page="perPage"
          v-model="currentPage"
          :total-rows="rows"></b-pagination>
      </b-card>
    </div>

    <DisplayPdf />
    <GridVue />
    <LinePDF />
    <SinglePagePDF :fileName="fileName" :currentRecord="currentRecord" />
    <multi-pdf :fileName="fileName" />
    <EditRecordModal :currentRecord="currentEditRecord" />
    <main-graph />

    <b-modal size="lg" id="create-data-sets" centered hide-footer title="Create Data Sets">
      <div class="data-set-row" v-for="row of dataSetRows" :key="row.id">
        <div class="row">
          <div class="col-sm">{{ row.title }}</div>
        </div>
        <div class="row mt-2">
          <div class="col-sm">
            <b-form-group label="Number of results to return:">
              <p style="font-size: x-small">A limit of 10 will give you 5 sets.</p>
              <b-input type="number" v-model="row.limit"></b-input>
            </b-form-group>
          </div>
          <div class="col-sm">
            <b-form-group style="margin-top: 2em">
              <b-checkbox switch type="number" v-model="row.currentYear">Limit to current year</b-checkbox>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-sm">
            <b-form-group style="margin-top: 1em">
              <b-checkbox switch type="number" v-model="row.ignoreVariety">Ignore variety</b-checkbox>
            </b-form-group>
          </div>
        </div>
      </div>
      <div style="text-align: left">
        <b-button @click="createDataSets" size="sm">Create Temporary Data Sets</b-button>
      </div>
    </b-modal>

    <b-modal id="edit-data-set" centered hide-footer title="Save Data Set">
      <b-form @submit.prevent="saveDataSet" style="text-align: left">
        <b-form-group label="Number of results to return:">
          <b-input type="number" v-model="currentDataSet.limit"></b-input>
        </b-form-group>

        <b-form-group>
          <b-checkbox switch type="number" v-model="currentDataSet.currentYear">Limit to current year</b-checkbox>
        </b-form-group>

        <b-form-group>
          <b-checkbox switch type="number" v-model="currentDataSet.ignoreVariety">Ignore variety</b-checkbox>
        </b-form-group>

        <b-button size="sm" type="submit">Save</b-button>
      </b-form>
    </b-modal>

    <!-- edit's plantpart  -->
    <b-modal id="edit-plant-part" centered hide-footer title="Edit Plant Part">
      <b-form @submit.prevent="savePlantPart">
        <b-form-group v-for="row of currentPlantParts" :key="row.id" :label="`Sample Number: ${row.sampleNumber}`">
          <b-select :options="['Leaf (young)', 'Leaf (old)', 'Leaf (middle)']" v-model="row.plantPart"></b-select>
        </b-form-group>
        <b-button variant="success" type="submit" size="sm">Save</b-button>
      </b-form>
    </b-modal>

    <!-- edit set info  -->
    <b-modal id="edit-sample-info" centered hide-footer title="Edit Sample Info">
      <b-form @submit.prevent="saveTestMetadata">
        <b-form-group label="Remarks:" label-for="comments">
          <b-form-input id="comments" type="text" placeholder="comments" v-model="currentTest.comments"></b-form-input>
        </b-form-group>

        <b-form-group label="Crop State:" label-for="crop-state">
          <b-form-input
            id="crop-state"
            type="text"
            placeholder="crop state"
            v-model="currentTest.disease"></b-form-input>
        </b-form-group>

        <b-form-group label="Health Score:" label-for="health-score" :description="currentTest.healthScore">
          <b-input-group prepend="0" append="10">
            <b-form-input
              id="health-score"
              type="range"
              min="1"
              max="10"
              v-model="currentTest.healthScore"></b-form-input>
          </b-input-group>
        </b-form-group>

        <b-form-group label="Treatment:" label-for="treatment">
          <b-form-input
            id="treatment"
            type="text"
            placeholder="treatment"
            v-model="currentTest.treatment"></b-form-input>
        </b-form-group>

        <b-form-group label="Phenological Phase:" label-for="phen-stage">
          <b-form-select id="phen-stage" v-model="currentTest.phenologicalPhase">
            <optgroup label="Perennial">
              <option>Bud Break</option>
              <option>Bloom</option>
              <option>Fruit Set</option>
              <option>Fruit Fill</option>
              <option>Pre Harvest</option>
              <option>Post Harvest</option>
            </optgroup>

            <optgroup label="Annuals">
              <option>Emergence</option>
              <option>Frame Buidling</option>
              <option>Flowering</option>
              <option>Fruit Set</option>
              <option>Fruit Fill</option>
              <option>Harvest</option>
            </optgroup>
          </b-form-select>
        </b-form-group>

        <!-- <b-form-group label="Attach Images:">
            <b-form-file v-model="images" accept="image/*" multiple placeholder="Choose a file or drop it here..."></b-form-file>
          </b-form-group> -->
        <b-button class="mr-1" type="submit">Save</b-button>
        <b-button class="mr-1" @click="$bvModal.hide('edit-sample-info')">Cancel</b-button>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { dataset, pdf, search } from "../modules/api";
// import { pdf } from "../modules/pdf";

import DisplayPdf from "../components/DisplayPdf";
import EditRecordModal from "../components/EditRecordModal";
import GridVue from "../components/GridVue";
import LinePDF from "../components/LinePDF";
import SinglePagePDF from "../components/SinglePagePDF";

import { mapActions, mapGetters, mapMutations } from "vuex";
import MainGraph from "../components/MainGraph.vue";
import MultiPdf from "../components/MultiPdf.vue";
import { ChlOrder } from "../modules/orderInt";
import { randomId, sortCompare } from "../modules/utils";

// possibly have plot name show up when legend is empty done
// different shapes for elements in the same group done
// drag and drop on element list done
// edit hieght of groups
// ratios in the grid view
// advanced search for datasets done
// export main graph as pdf done
// fix downlaod as png done
// fix parse int bug in math done

export default {
  name: "search",
  data() {
    return {
      string: "",
      fields: [
        { key: "sampleDate", sortable: true },
        { key: "businessName", sortable: true },
        { key: "locationName", sortable: true },
        { key: "plotName", sortable: true },
        {
          key: "cropName",
          label: "Crop",
          sortable: true,
        },
        {
          key: "varietyName",
          label: "Variety",
          sortable: true,
        },
        {
          key: "labName",
          label: "Lab",
          sortable: true,
        },
        {
          key: "show_details",
          label: "Details",
        },
      ],
      lastClickedRow: "",
      currentRecord: "",
      currentTest: {},
      images: [],
      fileName: "",
      currentPage: 1,
      perPage: 25,
      advancedSearch: false,
      showDataSets: false,
      dateWithin: "1 Week",
      dateSearchType: "Date Within",
      dateTo: "",
      dateFrom: "",
      businessName: "",
      locationName: "",
      plotName: "",
      cropName: "",
      varietyName: "",
      notify: {
        show: false,
        message: "",
      },
      currentSelect: null,
      currentEditRecord: {},
      currentDataSet: {},
      currentPlantParts: [],
      instructions: false,
    };
  },
  methods: {
    async searchRecords() {
      this.searchDataSets();

      let params = {};

      if (this.dateTo) {
        params.dateTo = this.dateTo;
        params.dateSearchType = this.dateSearchType;
        if (this.dateSearchType === "Date Within") {
          params.dateWithin = this.dateWithin;
        } else {
          params.dateFrom = this.dateFrom;
        }
      }

      let keys = ["businessName", "locationName", "plotName", "cropName", "varietyName"];
      for (let key of keys) {
        if (this[key]) {
          params[key] = this[key];
        }
      }

      if (!this.string && Object.keys(params).length <= 0) {
        return;
      }

      let records = await search.records(this.string, params);
      if (records.data.success && records.data.orders.length > 0) {
        records.data.orders.forEach(x => {
          x.setId = x.setId || randomId();
        });
        this.setCurrentSearchResults(records.data.orders.map(x => ({ ...x, selected: false })));
        this.setCurrentAddresses(records.data.addresses);
        this.setCurrentList([]);
        if (records.data.orders.length >= 500) {
          this.notify.message = "Returned more than 500 results. Please refine your search.";
          this.notify.show = true;
        } else {
          this.notify.show = false;
        }
      } else {
        this.notify.message = "Failed to find any results. Try another search.";
        this.notify.show = true;
      }
    },
    async searchDataSets() {
      let params = [];

      let keys = ["businessName", "locationName", "plotName", "cropName", "varietyName"];
      for (let key of keys) {
        if (this[key]) {
          params[key] = this[key];
        }
      }

      // if (!this.string && params.length <= 0) {
      //   return;
      // }

      let res = await dataset.get({ string: this.string, ...params });

      if (res.data.success && res.data.datasets.length > 0) {
        let dataSets = res.data.datasets.map(x => x.dataSet);
        this.setCurrentDataSets([
          {
            id: "currentSelection",
            title: "Current Selection",
            color: "darkgreen",
            selected: false,
          },
          ...dataSets,
        ]);
      }
    },
    async fetchDataSet(dataSet, saveData) {
      let params = {};
      let keys = ["businessName", "locationName", "plotName", "cropName", "limit"];
      if (!dataSet.ignoreVariety) {
        keys.push("varietyName");
      }
      for (let key of keys) {
        if (dataSet[key]) {
          params[key] = dataSet[key];
        }
      }

      if (dataSet.currentYear) {
        params.currentYear = true;
      }

      let records = await search.records("", params);

      if (records.data.success && records.data.orders.length > 0) {
        records.data.orders.forEach(x => {
          x.setId = x.setId || randomId();
        });
        if (saveData) {
          this.setCurrentSearchResults(records.data.orders.map(x => ({ ...x, selected: true })));
          this.setCurrentAddresses(records.data.addresses);
          this.setCurrentList(this.searchResults.map(x => x.externalId));
        }
      }
      return records;
    },

    async saveDataSet() {
      let json = { ...this.currentDataSet };
      delete json.externalId;
      delete json.color;
      let res = await dataset.createUpdate({
        title: this.currentDataSet.title,
        externalId: this.currentDataSet.id,
        createdBy: this.userId,
        dataSet: json,
      });

      this.mainNotify({
        message: res.data.message,
        variant: res.data.success ? "success" : "warn",
      });

      if (res.data.success) {
        this.$bvModal.hide("edit-data-set");
        this.updateDataSet({ id: this.currentDataSet.id, dataSet: this.currentDataSet });
      }
    },

    async deleteDataSet(externalId) {
      let res = await dataset.delete(externalId);

      this.mainNotify({
        message: res.data.message,
        variant: res.data.success ? "success" : "warn",
      });

      if (res.data.success) {
        this.setCurrentDataSets(this.currentDataSets.filter(x => x.id !== externalId));
      }
    },

    async multiPdf(dataSet) {
      await this.fetchDataSet({ ...dataSet, limit: 8 }, true);
      this.openPage("multiPdf");
      await this.$nextTick();
      let svg = document.getElementById("svg-multi-pdf").innerHTML;
      let fileName = `${dataSet.businessName} - ${dataSet.locationName} - ${dataSet.plotName} - ${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`;

      this.closePage("multiPdf");
      await this.$nextTick();
      fileName = prompt("Is this ok for a file name?", fileName);
      if (!fileName) {
        return;
      }
      await this.downloadPdf([svg], fileName);
    },

    async linePdf(dataSet) {
      await this.fetchDataSet(dataSet, true);
      this.openPage("linePdf");
      await this.$nextTick();
      let svg = document.getElementById("svg-cont").innerHTML;
      let svg2 = document.getElementById("svg-cont-2").innerHTML;
      let fileName = `${dataSet.businessName} - ${dataSet.locationName} - ${dataSet.plotName} - ${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`;

      this.closePage("linePdf");
      await this.$nextTick();
      fileName = prompt("Is this ok for a file name?", fileName);
      if (!fileName) {
        return;
      }
      await this.downloadPdf([svg, svg2], fileName);
    },

    tableSelect(items) {
      if (!items[0]) {
        return;
      }

      let setIds = items.map(x => x.setId);

      for (let id of setIds) {
        let records = this.searchResults.filter(x => x.setId === id);
        records.forEach(record => {
          record.selected = this.currentSelect === null ? !record.selected : this.currentSelect;
        });
      }

      this.currentSelect = null;
      this.$refs.searchRes.clearSelected();
      this.setCurrentList(this.searchResults.filter(x => x.selected).map(x => x.externalId));
    },

    openPDFModal(externalId) {
      this.openPage("singlePagePdf");
      let test = this.searchResults.find(x => x.externalId === externalId);
      this.fileName = `${test.businessName}-${test.locationName}-${test.plotName}`;
      this.currentRecord = externalId;
    },

    formatDate(date) {
      return date ? new Date(date).toLocaleDateString() : "";
    },
    editRecord(externalId) {
      this.currentEditRecord = this.searchResults.find(x => x.externalId === externalId);
      this.$bvModal.show("edit-records");
    },

    editPlantPartModal(record) {
      let id = record.id.split(",");
      let sampleNumber = record.sampleNumber.split(",");
      let plantPart = record.plantPart.split(",");

      id = id.map(x => x && x.trim());
      sampleNumber = sampleNumber.map(x => x && x.trim());
      plantPart = plantPart.map(x => x && x.trim());

      this.currentPlantParts = id.map((id, i) => ({ id, sampleNumber: sampleNumber[i], plantPart: plantPart[i] }));

      this.$bvModal.show("edit-plant-part");
    },

    editInfo(id) {
      this.currentTest = this.searchResults.find(x => x.setId === id);
      if (this.currentTest.healthScore) {
        this.currentTest.healthScore = this.currentTest.healthScore.toString();
      }
      this.$bvModal.show("edit-sample-info");
    },

    async saveTestMetadata() {
      let toUpdate = this.searchResults.filter(x => x.setId === this.currentTest.setId);
      let testData = [];
      for (let test of toUpdate) {
        testData.push({
          externalId: test.externalId,
          setId: test.setId,
          comments: this.currentTest.comments,
          disease: this.currentTest.disease,
          healthScore: this.currentTest.healthScore,
          treatment: this.currentTest.treatment,
          phenologicalPhase: this.currentTest.phenologicalPhase,
        });
      }
      let orderRes = await ChlOrder.updateMeta(testData);
      this.mainNotify({
        message: orderRes.data.message,
        variant: orderRes.data.success ? "success" : "warn",
      });
      this.$bvModal.hide("edit-sample-info");
    },

    async savePlantPart() {
      let res = await ChlOrder.updatePlantPart(this.currentPlantParts);
      this.mainNotify(res.data);
      if (res.data.success) {
        this.$bvModal.hide("edit-plant-part");
      }
    },

    async deleteRow(id) {
      let proceed = await this.$bvModal.msgBoxConfirm("Are you completely sure about this??", { centered: true });

      if (proceed) {
        let ids = id.split(",");

        for (let id of ids) {
          id = id.trim();
          let res = await ChlOrder.deleteLineItem(id);
          this.notify.message = res.data.message;
          this.notify.show = true;
        }

        this.setCurrentSearchResults(this.searchResults.filter(x => !ids.includes(x.id)));
      }
    },

    async massPdfs() {
      let svgs = [];
      for (let setId of this.currentSetIds) {
        const record = this.allRecords.find(record => record.setId === setId);

        if (record) {
          this.currentRecord = record.externalId;
        }

        await this.$nextTick();
        svgs.push(document.getElementById("svg-single-page").innerHTML);
      }

      await this.downloadPdf(svgs, `Sap-PDF-${new Date().toISOString().slice(0, 10)}.pdf`);
    },

    async downloadPdf(svgs, fileName) {
      let res = await pdf.createLinePdf({ svgs });

      const blob = new Blob([res.data], { type: "application/pdf" });

      // process to auto download it
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement("a");
      document.body.append(link);
      link.href = fileURL;
      link.download = fileName;
      link.click();
      setTimeout(function () {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(fileURL);
        document.body.removeChild(link);
      }, 100);
    },

    createDataSets() {
      let dataSets = this.dataSetRows.map(x => ({ ...x, id: randomId() }));
      this.setCurrentDataSets([...this.currentDataSets, ...dataSets]);
      this.$bvModal.hide("create-data-sets");
    },
    ...mapActions({ mainNotify: "notify" }),
    ...mapMutations([
      "setCurrentSearchResults",
      "setCurrentAddresses",
      "setCurrentList",
      "openPage",
      "setCurrentDataSets",
      "updateDataSet",
      "closePage",
    ]),
  },
  computed: {
    sortCompare() {
      return sortCompare;
    },
    rows() {
      return this.concatOrders.length;
    },
    concatOrders() {
      let orders = [];
      let processedIds = [];
      let list = [
        "plantPart",
        "sampleNumber",
        "barcode",
        "healthScore",
        "disease",
        "treatment",
        "phenologicalPhase",
        "samplingDate",
        "id",
      ];

      for (let order of this.searchResults) {
        if (processedIds.includes(order.externalId)) {
          continue;
        }

        if (!order.setId) {
          orders.push(order);
          processedIds.push(order.externalId);
          continue;
        }

        let pairs = this.searchResults.filter(x => x.setId === order.setId && x.externalId !== order.externalId);

        if (pairs.length > 0) {
          let inner = {
            ...order,
          };
          for (let key of list) {
            let isEqual = pairs.reduce((prev, current) => prev[key] == current[key], inner);
            if (!isEqual) {
              inner[key] = `${inner[key]}, ${pairs.map(x => x[key]).join(", ")}`;
            }
          }

          orders.push(inner);
          processedIds = processedIds.concat(pairs.map(x => x.externalId));

          processedIds.push(order.externalId);
        } else {
          orders.push(order);
          processedIds.push(order.externalId);
        }
      }
      return orders;
    },
    dataSetRows() {
      let rows = [];
      for (let row of this.allRecords) {
        let id = `${row.businessName}${row.locationName}${row.plotName}${row.cropName}`;
        if (rows.map(x => x.id).includes(id)) {
          continue;
        }
        rows.push({
          title: `${row.businessName} : ${row.locationName} : ${row.plotName} : ${row.cropName}`,
          id,
          limit: 30,
          ignoreVariety: true,
          currentYear: true,
          externalId: row.externalId,
          businessName: row.businessName,
          locationName: row.locationName,
          plotName: row.plotName,
          cropName: row.cropName,
          varietyName: row.varietyName,
        });
      }

      return rows;
    },
    displayedDataSets() {
      return this.currentDataSets.filter(x => x.id !== "currentSelection");
    },
    ...mapGetters({
      searchResults: "currentSearchResults",
      addresses: "currentAddresses",
      multiPDFLines: "currentRecordList",
      isAdmin: "isAdmin",
      currentRecordList: "currentRecordList",
      currentSetIds: "currentSetIds",
      currentDataSets: "currentDataSets",
      allRecords: "allRecords",
      userId: "userId",
    }),
  },
  components: { DisplayPdf, GridVue, LinePDF, SinglePagePDF, EditRecordModal, MultiPdf, MainGraph },
  mounted() {
    let query = this.$route.query;
    let keys = [
      "string",
      "locationName",
      "businessName",
      "plotName",
      "varietyName",
      "cropName",
      "dateTo",
      "dateFrom",
      "dateSearchType",
      "dateWithin",
    ];

    let go = false;
    for (let key of keys) {
      if (query[key]) {
        this[key] = query[key];
        go = true;
      }
    }

    if (go) {
      this.searchRecords();
    }
  },
};
</script>

<style lang="scss" scoped>
.advanced {
  float: right;
}

.data-set-row {
  background-color: #f8f9fa;
  border-radius: 5px;
  padding: 1em;
  text-align: left;
  border: 1px solid #ccc;
  margin-bottom: 1em;
}
.instructions {
  font-size: smaller;
}
</style>
